import { useEffect, useMemo, useRef, useState } from "react";
import { Box } from "@primer/react";
import {
  ArrowRightIcon,
  ArrowLeftIcon,
  BookIcon,
  ChevronDownIcon,
  FileIcon,
  GraphIcon,
  HomeIcon,
  KeyIcon,
  PlusIcon,
  PulseIcon,
  RocketIcon,
  SearchIcon,
  XIcon,
} from "@primer/octicons-react";
import { useAuth } from "../contexts/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import { default_avatar } from "../assets";
import { addDoc, collection, doc, Timestamp } from "firebase/firestore";
import { db } from "../config/firebase";
import { sampleInvoice, sampleTemplateData } from "../templates/sampleInvoice";
import { extractVariablesFromJson } from "../utils/jsonUtils";

interface HeaderProps {
  editorMode?: boolean;
}

const SEARCH_ACTIONS = [
  { label: "Home", description: "Create a PDF or review your account", path: "/dashboard", icon: HomeIcon },
  { label: "Templates", description: "Manage and edit saved templates", path: "/dashboard/templates", icon: FileIcon },
  { label: "Analytics", description: "Review usage, tokens, and activity", path: "/dashboard/analytics", icon: PulseIcon },
  { label: "API Keys", description: "Create and manage API credentials", path: "/dashboard/api-keys", icon: KeyIcon },
  { label: "Usage & Billing", description: "Review usage, tokens, and wallet balance", path: "/dashboard/usage", icon: GraphIcon },
  { label: "API Documentation", description: "Read integration guides and examples", path: "/api-documentation?section=generate-pdf", icon: BookIcon },
] as const;

function getPageTitle(pathname: string) {
  if (pathname === "/dashboard") return "Home";
  if (pathname === "/dashboard/templates") return "Templates";
  if (pathname === "/dashboard/analytics") return "Analytics";
  if (pathname === "/dashboard/templates/prompt") return "Create with AI";
  if (pathname.startsWith("/dashboard/templates/edit/") || pathname === "/dashboard/templates/new") return "Template Editor";
  if (pathname === "/dashboard/api-keys") return "API Keys";
  if (pathname.startsWith("/dashboard/api-usage/")) return "API Usage";
  if (pathname === "/dashboard/usage") return "Usage & Billing";
  if (pathname === "/dashboard/settings") return "Profile Settings";
  if (pathname === "/api-documentation") return "API Documentation";
  return "ForgePDF";
}

export function Header({ editorMode = false }: HeaderProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState<"new" | "account" | null>(null);
  const [query, setQuery] = useState("");
  const newMenuRef = useRef<HTMLDivElement>(null);
  const accountMenuRef = useRef<HTMLDivElement>(null);

  const filteredActions = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return SEARCH_ACTIONS;
    return SEARCH_ACTIONS.filter(
      (action) =>
        action.label.toLowerCase().includes(normalizedQuery) ||
        action.description.toLowerCase().includes(normalizedQuery),
    );
  }, [query]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setActiveMenu(null);
        setIsSearchOpen(true);
      }
      if (event.key === "Escape") {
        setIsSearchOpen(false);
        setActiveMenu(null);
        setQuery("");
      }
    };
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!newMenuRef.current?.contains(target) && !accountMenuRef.current?.contains(target)) {
        setActiveMenu(null);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("pointerdown", handlePointerDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("pointerdown", handlePointerDown);
    };
  }, []);

  useEffect(() => {
    setActiveMenu(null);
    setIsSearchOpen(false);
    setQuery("");
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const handleNewApiKey = async () => {
    try {
      const userRef = doc(db, "users", user!.uid);
      const apiKey = crypto.randomUUID().replace(/-/g, "");
      await addDoc(collection(db, "api_keys"), {
        api_key: apiKey,
        user_ref: userRef,
        created_at: new Date(),
      });
      if (location.pathname === "/dashboard/api-keys") navigate(0);
      else navigate("/dashboard/api-keys");
    } catch (error) {
      console.error("Error generating API key:", error);
    }
  };

  const handleNewTemplate = async () => {
    try {
      const userRef = doc(db, "users", user!.uid);
      const now = Timestamp.now();
      const variables = extractVariablesFromJson(sampleTemplateData);
      const docRef = await addDoc(collection(db, "templates"), {
        name: "New Template",
        html: sampleInvoice,
        data_json: JSON.stringify(sampleTemplateData, null, 2),
        variables,
        config: {
          margine_bottom: 4,
          margine_left: 4,
          margine_right: 4,
          margine_top: 4,
          orientation: "Landscape",
          paper_size: "Letter",
          print_background: true,
        },
        user_ref: userRef,
        created_at: now,
        updated_at: now,
      });
      navigate(`/dashboard/templates/edit/${docRef.id}`);
    } catch (error) {
      console.error("Error creating template:", error);
    }
  };

  const closeSearch = () => {
    setIsSearchOpen(false);
    setQuery("");
  };

  return (
    <Box as="header" className="app-header">
      {editorMode && (
        <button
          type="button"
          className="app-editor-back"
          onClick={() => navigate("/dashboard/templates")}
          aria-label="Back to templates"
          title="Back to templates"
        >
          <ArrowLeftIcon size={16} />
        </button>
      )}
      <span className="app-header-title">{getPageTitle(location.pathname)}</span>
      <Box className="app-header-spacer" />

      {!editorMode && (
        <button
          type="button"
          className="app-search-trigger"
          onClick={() => {
            setActiveMenu(null);
            setIsSearchOpen(true);
          }}
        >
          <SearchIcon size={16} />
          <span className="app-search-label">Search…</span>
          <kbd>⌘K</kbd>
        </button>
      )}

      <div className="app-popover-anchor" ref={newMenuRef}>
        <button
          type="button"
          className="app-header-button"
          aria-expanded={activeMenu === "new"}
          aria-haspopup="menu"
          onClick={() => setActiveMenu((current) => current === "new" ? null : "new")}
        >
          <PlusIcon size={16} />
          <span>New</span>
        </button>
        {activeMenu === "new" && (
          <div className="app-popover app-create-menu" role="menu">
            <div className="app-popover-heading">Create new</div>
            <button type="button" role="menuitem" onClick={handleNewTemplate}>
              <span className="app-menu-icon"><FileIcon size={17} /></span>
              <span><strong>Blank template</strong><small>Start from editable HTML</small></span>
            </button>
            <button type="button" role="menuitem" onClick={() => navigate("/dashboard")}>
              <span className="app-menu-icon is-accent"><RocketIcon size={17} /></span>
              <span><strong>Create with AI</strong><small>Generate from a prompt or image</small></span>
            </button>
            <button type="button" role="menuitem" onClick={handleNewApiKey}>
              <span className="app-menu-icon"><KeyIcon size={17} /></span>
              <span><strong>API key</strong><small>Authenticate a new integration</small></span>
            </button>
          </div>
        )}
      </div>

      <div className="app-popover-anchor" ref={accountMenuRef}>
        <button
          type="button"
          className="app-account-trigger"
          aria-label="Open account menu"
          aria-expanded={activeMenu === "account"}
          aria-haspopup="menu"
          onClick={() => setActiveMenu((current) => current === "account" ? null : "account")}
        >
          <img src={user?.photoURL || default_avatar} alt="" />
          <ChevronDownIcon size={13} />
        </button>
        {activeMenu === "account" && (
          <div className="app-popover app-account-menu" role="menu">
            <div className="app-account-summary">
              <img src={user?.photoURL || default_avatar} alt="" />
              <span><small>Signed in as</small><strong>{user?.email}</strong></span>
            </div>
            <div className="app-menu-divider" />
            <button type="button" role="menuitem" onClick={() => navigate("/dashboard/settings")}>
              <span>Profile settings</span><ArrowRightIcon size={14} />
            </button>
            <button type="button" role="menuitem" className="is-danger" onClick={handleLogout}>Sign out</button>
          </div>
        )}
      </div>

      {isSearchOpen && (
        <div className="app-search-overlay" role="presentation" onMouseDown={(event) => {
          if (event.target === event.currentTarget) closeSearch();
        }}>
          <section className="app-search-modal" role="dialog" aria-modal="true" aria-labelledby="app-search-title">
            <div className="app-search-modal-head">
              <span id="app-search-title">Search ForgePDF</span>
              <button type="button" aria-label="Close search" onClick={closeSearch}><XIcon size={16} /></button>
            </div>
            <label className="app-search-input">
              <SearchIcon size={18} />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search pages and tools"
                autoFocus
              />
              <kbd>ESC</kbd>
            </label>
            <div className="app-search-results">
              {filteredActions.map((action) => {
                const Icon = action.icon;
                return (
                  <button
                    type="button"
                    className="app-search-result"
                    key={action.path}
                    onClick={() => {
                      navigate(action.path);
                      closeSearch();
                    }}
                  >
                    <span className="app-search-result-icon"><Icon size={17} /></span>
                    <span className="app-search-result-copy"><strong>{action.label}</strong><small>{action.description}</small></span>
                    <ArrowRightIcon size={14} />
                  </button>
                );
              })}
              {filteredActions.length === 0 && <div className="app-search-empty">No matching pages</div>}
            </div>
            <div className="app-search-footer"><span>Navigate with your keyboard</span><span><kbd>↵</kbd> Open</span></div>
          </section>
        </div>
      )}
    </Box>
  );
}
